import React from "react";
import { useParams, Link } from "../router";
import AssetVisual from "../components/AssetVisual";
import EMICalculator from "../components/EMICalculator";
import PropertyCard from "../components/PropertyCard";
import BankStrip from "../components/BankStrip";
import { getProperty, properties } from "../data/properties";

export default function PropertyDetailPage() {
  const { id } = useParams();
  const p = getProperty(id);

  if (!p) {
    return (
      <section className="section">
        <div className="shell center">
          <h2 className="h-lg">This listing is no longer live.</h2>
          <p className="muted" style={{ margin: "12px 0 24px" }}>It may have sold, or been removed under our zero-dead-inventory rule.</p>
          <Link to="/properties" className="btn btn-ink">Back to portfolio <span className="arr">→</span></Link>
        </div>
      </section>
    );
  }

  const others = properties.filter((x) => x.id !== p.id).slice(0, 3);

  const specs = [
    ["Asset class", p.assetClass],
    ["Location", `${p.locality}, ${p.city}`],
    [p.area ? "Extent" : "Extent", `${p.area} ${p.areaUnit}`],
    ...(p.beds ? [["Bedrooms", p.beds], ["Bathrooms", p.baths]] : []),
    ["Verified", `${p.verifiedDays} day${p.verifiedDays > 1 ? "s" : ""} ago`],
    ["Title", "Aditi Certified ◈"],
  ];

  return (
    <>
      {/* Visual hero */}
      <section style={{ position: "relative" }}>
        <div style={{ aspectRatio: "21/9", maxHeight: 560 }}>
          <AssetVisual tone={p.tone} accent={p.accent} />
        </div>
        <div className="shell" style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", justifyContent: "flex-end", paddingBottom: 32 }}>
          <div className="flexrow" style={{ gap: 10, marginBottom: 14 }}>
            <span className="badge live" style={{ background: "rgba(11,11,12,0.6)", color: "#7CE0A8", borderColor: "transparent" }}><span className="dot" /> Live & Available · verified {p.verifiedDays}d ago</span>
            <span className="badge" style={{ background: "rgba(11,11,12,0.6)" }}>◈ Aditi Certified Title Check</span>
            {p.construction && <span className="badge" style={{ background: "rgba(11,11,12,0.6)" }}>Design + Build available</span>}
          </div>
          <h1 className="display" style={{ color: "#fff", fontSize: "clamp(2.2rem,5vw,4rem)" }}>{p.title}</h1>
          <p style={{ color: "rgba(249,249,251,0.85)", marginTop: 8 }}>{p.locality}, {p.city}</p>
        </div>
      </section>

      <section className="section" style={{ paddingTop: "clamp(40px,5vw,64px)" }}>
        <div className="shell">
          <div className="grid" style={{ gridTemplateColumns: "1.4fr 1fr", gap: "clamp(32px,4vw,64px)", alignItems: "start" }}>
            {/* Left: detail */}
            <div className="reveal">
              <div className="spaced" style={{ alignItems: "flex-end", marginBottom: 28 }}>
                <div>
                  <span className="eyebrow">{p.assetClass}</span>
                  <div style={{ fontFamily: "var(--serif)", fontSize: "clamp(2rem,4vw,3rem)", color: "var(--ink)", marginTop: 8 }}>{p.priceLabel}</div>
                </div>
                <span className="muted">{p.beds ? `${p.beds} BR · ${p.baths} Bath · ` : ""}{p.area} {p.areaUnit}</span>
              </div>

              <p className="lead">{p.blurb}</p>

              <h3 className="h-md" style={{ margin: "40px 0 18px" }}>The essentials</h3>
              <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 0, border: "1px solid var(--alabaster-dim)", borderRadius: 6, overflow: "hidden" }}>
                {specs.map(([k, v], i) => (
                  <div key={k + i} style={{ padding: "18px 22px", borderBottom: "1px solid var(--alabaster-dim)", borderRight: i % 2 === 0 ? "1px solid var(--alabaster-dim)" : "none" }}>
                    <div className="muted" style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>{k}</div>
                    <div style={{ fontWeight: 600, marginTop: 4 }}>{v}</div>
                  </div>
                ))}
              </div>

              <h3 className="h-md" style={{ margin: "40px 0 18px" }}>Highlights</h3>
              <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {p.highlights.map((h) => (
                  <div key={h} style={{ borderLeft: "1px solid var(--champagne)", paddingLeft: 14, fontSize: "0.96rem" }}>{h}</div>
                ))}
              </div>

              {/* Trust band */}
              <div style={{ marginTop: 44, background: "var(--obsidian)", color: "var(--paper-ink)", borderRadius: 8, padding: "clamp(24px,3vw,36px)" }}>
                <span className="eyebrow gold">Aditi Legal Shield</span>
                <h3 className="h-md" style={{ margin: "12px 0 12px", color: "var(--paper-ink)" }}>This asset passed our secondary title check.</h3>
                <p className="muted" style={{ fontSize: "0.95rem" }}>
                  Encumbrance, title lineage, litigation history and statutory
                  approvals were independently reviewed by Aditi's in-house legal
                  team — over and above the seller's own paperwork. The Certified
                  Title Check badge is only issued once that review clears.
                </p>
                <div className="flexrow" style={{ marginTop: 20, gap: 10 }}>
                  <span className="badge"><span className="dot" /> Encumbrance clear</span>
                  <span className="badge"><span className="dot" /> Title lineage traced</span>
                  <span className="badge"><span className="dot" /> No active litigation</span>
                </div>
              </div>

              {p.construction && (
                <div style={{ marginTop: 28 }}>
                  <h3 className="h-md" style={{ marginBottom: 12 }}>Build it with us</h3>
                  <p className="muted">
                    This is a build-ready asset. Pair it with Aditi's design-build
                    studio for a turnkey home — architecture, engineering, and
                    construction on a fixed cost and timeline.
                  </p>
                  <Link to="/contact" className="btn-line" style={{ marginTop: 14 }}>Plan a build on this plot <span className="arr">→</span></Link>
                </div>
              )}
            </div>

            {/* Right: sticky EMI + CTA */}
            <div className="detail-rail" style={{ position: "sticky", top: 104, display: "flex", flexDirection: "column", gap: 20 }}>
              <EMICalculator price={p.price} />
              <div className="card" style={{ padding: 24 }}>
                <h4 style={{ fontFamily: "var(--sans)", fontSize: "0.74rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "var(--champagne)", marginBottom: 14 }}>Direct bank alliances</h4>
                <BankStrip />
                <Link to="/contact" className="btn btn-ink" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}>Book a private viewing <span className="arr">→</span></Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="shell">
          <div className="spaced" style={{ marginBottom: 36 }}>
            <h2 className="h-lg">More from the portfolio</h2>
            <Link to="/properties" className="btn-line">View all <span className="arr">→</span></Link>
          </div>
          <div className="grid props-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
            {others.map((o) => <PropertyCard key={o.id} p={o} />)}
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 940px) {
          .section .grid[style*="1.4fr"] { grid-template-columns: 1fr !important; }
          .detail-rail { position: static !important; }
        }
        @media (max-width: 980px) { .props-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 620px) { .props-grid { grid-template-columns: 1fr !important; } .grid[style*="1fr 1fr"] { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
}
